import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, Activity, Brain, ArrowRight, Sparkles, Mic, Hand } from "lucide-react";
import { useChatbot } from "@/context/ChatbotContext";
import { useMental } from "@/context/MentalContext";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};
const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

// The three "front door" demos — placed immediately after the hero so any
// visitor can instantly understand + try what the platform does.
export default function TryDemos() {
  const { openChatbot } = useChatbot();
  const { openMental } = useMental();

  const CARDS = [
    {
      testId: "try-health-score",
      Icon: Activity,
      badge: "3-minute AI check-in",
      title: "Get your Health Score",
      body: "Answer 16 questions. Our AI returns a 0–100 Health Score, a 6-dimension Health Twin, and a 7-day Indian diet plan.",
      accent: "#22d3ee",
      gradient: "from-cyan-500/25 to-blue-500/10",
      action: openChatbot,
      cta: "Try Health Score",
    },
    {
      testId: "try-mental-wellness",
      Icon: Heart,
      badge: "11-question mood scan",
      title: "Check your Mental Wellness",
      body: "A gentle guided check-in for stress, sleep and emotional wellbeing. Distress signals route to real Indian helplines.",
      accent: "#f472b6",
      gradient: "from-rose-500/25 to-fuchsia-500/10",
      action: openMental,
      cta: "Try Mental Wellness",
    },
    {
      testId: "try-neurorehab",
      Icon: Brain,
      badge: "Cognitive rehabilitation",
      title: "Try NeuroRehab AI",
      body: "Adaptive brain-training for memory, attention, speed, problem-solving and executive function. Perfect after stroke or brain injury.",
      accent: "#a78bfa",
      gradient: "from-violet-500/25 to-fuchsia-500/10",
      to: "/neurorehab",
      cta: "Try NeuroRehab",
    },
    {
      testId: "try-neurospeak",
      Icon: Mic,
      badge: "Communication rehab · voice AI",
      title: "Try NeuroSpeak AI",
      body: "Record your voice, watch Whisper AI transcribe it live, and see instant pronunciation-clarity feedback. Graduated cueing 0→4. Non-diagnostic.",
      accent: "#34d399",
      gradient: "from-emerald-500/25 to-teal-500/10",
      to: "/neurospeak",
      cta: "Try NeuroSpeak",
    },
    {
      testId: "try-neuromotor",
      Icon: Hand,
      badge: "Motor-skill practice",
      title: "Try NeuroMotor AI",
      body: "Touchscreen target-reach, reaction and coordination tasks with adaptive difficulty. Left / right / both hand modes. Task-performance only.",
      accent: "#fb923c",
      gradient: "from-orange-500/25 to-amber-500/10",
      to: "/neuromotor",
      cta: "Try NeuroMotor",
    },
  ];

  return (
    <section
      id="try"
      data-testid="section-try-demos"
      className="relative py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">
              Try it in one tap
            </span>
          </div>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02]">
            Five demos.{" "}
            <span className="italic text-cyan-300">Zero signup.</span>
          </h2>
          <p className="mt-4 text-lg text-zinc-400 leading-relaxed max-w-xl">
            Everything here is free — nothing to install, no account needed.
            Pick a demo below and see what your AI health team can do in the
            next 3 minutes.
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4"
        >
          {CARDS.map((c) => (
            <motion.div
              key={c.testId}
              variants={item}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.25 }}
              className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br ${c.gradient} p-7 sm:p-8 flex flex-col`}
            >
              <div
                aria-hidden
                className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity"
                style={{ background: c.accent }}
              />
              <div className="relative flex-1">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center border"
                  style={{
                    background: `${c.accent}18`,
                    borderColor: `${c.accent}55`,
                  }}
                >
                  <c.Icon className="w-6 h-6" style={{ color: c.accent }} />
                </div>
                <div
                  className="mt-6 text-[10px] uppercase tracking-[0.25em] font-semibold"
                  style={{ color: c.accent }}
                >
                  {c.badge}
                </div>
                <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-white leading-tight">
                  {c.title}
                </h3>
                <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
                  {c.body}
                </p>
              </div>
              <div className="relative mt-8">
                {c.to ? (
                  <Link
                    to={c.to}
                    data-testid={c.testId}
                    className="inline-flex items-center gap-2 rounded-full bg-white text-black font-semibold px-6 py-3 text-sm hover:bg-cyan-100"
                  >
                    {c.cta}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <button
                    data-testid={c.testId}
                    onClick={c.action}
                    className="inline-flex items-center gap-2 rounded-full bg-white text-black font-semibold px-6 py-3 text-sm hover:bg-cyan-100"
                  >
                    {c.cta}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        <div className="mt-6 text-[11px] text-zinc-500 text-center">
          Educational only · Not a substitute for a qualified clinician
        </div>
      </div>
    </section>
  );
}
